<template>
    <Screen>
        <AppBar :title="title" />
        <Breadcrumbs
            :links="[
                {
                    to: $route('admin.blissdom.sections.index'),
                    title: 'Blissdom sections',
                },
            ]"
            :title="title"
        />
        <div class="card card-body" v-if="formSet">
            <div class="row">
                <div class="col-lg-6">
                    <h4>Details</h4>
                    <hr />
                    <form
                        action=""
                        method="post"
                        @submit.prevent="submit"
                        v-if="formSet"
                    >
                        <TextBox
                            name="name"
                            v-model="form.name"
                            :disabled="form.processing"
                            :error="form.errors.name"
                        />
                        <div class="form-group">
                            <label for="category_id">Category</label>
                            <AdvanceSelect
                                class="advance-dpdown"
                                :options="categories"
                                :reduce="(item) => item.id"
                                id="blissdom_categories"
                                label="title"
                                placeholder="Select category"
                                :disabled="form.processing"
                                :class="{
                                    'is-invalid': form.errors.category_id,
                                }"
                                v-model="form.category_id"
                            >
                            </AdvanceSelect>
                            <transition name="form-error">
                                <div
                                    class="error"
                                    v-if="form.errors.category_id"
                                    v-text="form.errors.category_id"
                                ></div>
                            </transition>
                        </div>
                        <TextBox
                            name="sort_order"
                            v-model="form.sort_order"
                            :disabled="form.processing"
                            :error="form.errors.sort_order"
                            helperText="Higher the number, higher the priority"
                        />
                        <FileInput
                            name="icon"
                            v-model="form.icon"
                            :fileUrl="form.icon_url"
                            :disabled="form.processing"
                            :error="form.errors.icon"
                            helperText="Ideal size is 120x120 pixels or aspect ratio of 1:1. Upload svg file for better quality"
                        />

                        <div class="form-group">
                            <label for="content_type">Content type</label>
                            <AdvanceSelect
                                class="advance-dpdown"
                                :options="blissdom_types"
                                :reduce="(item) => item.id"
                                id="blissdom_types"
                                label="title"
                                placeholder="Select content type"
                                :disabled="form.processing"
                                :class="{
                                    'is-invalid': form.errors.content_type,
                                }"
                                v-model="form.content_type"
                            >
                            </AdvanceSelect>
                            <transition name="form-error">
                                <div
                                    class="error"
                                    v-if="form.errors.content_type"
                                    v-text="form.errors.content_type"
                                ></div>
                            </transition>
                        </div>
                        <div class="content-type-help">
                            <div v-if="form.content_type == 'url'">
                                <p>
                                    Kindly make sure to add http:// or https://
                                    before the link.
                                </p>
                                <p>
                                    Content or website will be displayed in app
                                    as an iframe.
                                </p>
                                <p>Examples are provided below.</p>
                                <table class="table mb-0">
                                    <tr>
                                        <th>Example</th>
                                        <th>URL</th>
                                    </tr>
                                    <tr
                                        v-for="externalUrl in externalUrls"
                                        :key="externalUrl.url"
                                    >
                                        <td v-text="externalUrl.title"></td>
                                        <td v-text="externalUrl.url"></td>
                                    </tr>
                                </table>
                            </div>
                            <div v-if="form.content_type == 'text-editor'">
                                <p>
                                    Enter the content in the text editor. You
                                    can use HTML tags to format the content.
                                </p>
                                <p class="mb-0">
                                    You can also embed videos from YouTube,
                                    Vimeo, Facebook, and Dailymotion.
                                </p>
                            </div>
                            <div v-if="form.content_type == 'inapp-url'">
                                <p>
                                    It will redirect to the selected page when
                                    clicked. Use the links provided below.
                                </p>
                                <p class="text-danger">
                                    Use this feature carefully or else it will
                                    redirect to 404 page.
                                </p>
                                <table class="table mb-0">
                                    <tr>
                                        <th>Title</th>
                                        <th>URL</th>
                                    </tr>
                                    <tr
                                        v-for="inAppUrl in inAppUrls"
                                        :key="inAppUrl.url"
                                    >
                                        <td v-text="inAppUrl.title"></td>
                                        <td
                                            v-text="inAppUrl.url"
                                            class="text-primary"
                                            @click="form.content = inAppUrl.url"
                                        ></td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                        <div v-if="showContent">
                            <TextBox
                                v-if="form.content_type == 'text-editor'"
                                type="editor"
                                name="text-editor"
                                key="text-editor"
                                v-model="form.content"
                                :disabled="form.processing"
                                :error="form.errors.content"
                            />
                            <TextBox
                                v-else
                                name="content"
                                v-model.lazy="form.content"
                                :disabled="form.processing"
                                :error="form.errors.content"
                            />
                        </div>
                        <div class="row">
                            <div class="col-auto">
                                <StatusRadio
                                    v-model="form.isactive"
                                    :disabled="form.processing"
                                    :error="form.errors.isactive"
                                />
                            </div>
                            <div class="col-auto">
                                <StatusRadio
                                    label="Premium content"
                                    :radioLabel="['Yes', 'No']"
                                    name="premium_only"
                                    v-model="form.premium_only"
                                    :disabled="form.processing"
                                    :error="form.errors.premium_only"
                                />
                            </div>
                        </div>
                        <div class="mt-4 text-center">
                            <button
                                class="btn btn-primary w-25"
                                type="submit"
                                :disabled="form.processing"
                                @click.prevent="submit"
                            >
                                <div
                                    class="spinner-border spinner-border-sm"
                                    role="status"
                                    v-if="form.processing"
                                ></div>
                                <span v-else>Save</span>
                            </button>
                        </div>
                    </form>
                </div>
                <div class="col-lg-6" v-if="form.content_type !== 'inapp-url'">
                    <hr class="d-block d-lg-none" />
                    <h4>Preview</h4>
                    <hr />
                    <MobilePreview
                        :title="form.name"
                        :content="form.content"
                        :content_type="form.content_type"
                    />
                </div>
            </div>
        </div>
    </Screen>
</template>
<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import FileInput from "@/components/form/fileInput.vue";

import "@/helpers/summernote/summernote-bs4.min.js";
import "@/helpers/summernote/summernote-bs4.min.css";

import MobilePreview from "../components/mobilePreview.vue";

export default {
    components: {
        AdvanceSelect: vSelect,
        FileInput,
        MobilePreview,
    },
    props: {
        blissdom_types: {
            type: Array,
            default: () => [],
        },
        categories: {
            type: Array,
            default: () => [],
        },
        title: {
            type: String,
            default: "",
        },
        formData: {
            type: [Object, Array],
            default: () => {},
        },
        submitUrl: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            form: {},
            formSet: false,
            inAppUrls: [
                {
                    url: "/home",
                    title: "Home page (Blisscipline)",
                },
                {
                    url: "/blissdom",
                    title: "Blissdom section page",
                },
                {
                    url: "/blissboard",
                    title: "Blissboard page",
                },
                {
                    url: "/meditation-tracks",
                    title: "Meditation tracks page",
                },
                {
                    url: "/breathing-exercises",
                    title: "Breathing Exercise tracks page",
                },
            ],
            externalUrls: [
                {
                    title: "Youtube video",
                    url: "https://www.youtube.com/embed/{VIDEO_ID}",
                },
                {
                    title: "Vimeo video",
                    url: "https://player.vimeo.com/video/{VIDEO_ID}",
                },
                {
                    title: "Daily Motion video",
                    url: "https://www.dailymotion.com/embed/video/{VIDEO_ID}",
                },
                {
                    title: "Canva",
                    url: "https://www.canva.com/design/{DESIGN_ID}/view?embed",
                },
            ],
        };
    },
    mounted() {
        setTimeout(() => {
            this.form = this.$inertia.form(this.formData);
            this.form.icon = null;

            setTimeout(() => {
                this.formSet = true;
            });
        });
    },
    methods: {
        submit() {
            this.form.post(this.submitUrl);
        },
    },
    computed: {
        showContent() {
            const exclude = ["breathing-exercise", "meditation-tracks"];
            if (exclude.includes(this.form.content_type)) {
                return false;
            }
            return true;
        },
    },
};
</script>
<style lang="scss" scoped>
.content-type-help {
    padding: 1rem;
    border: 2px solid var(--warning);
    border-radius: var(--border-radius);
    margin-bottom: 1rem;
}
</style>
